import { Virtualizer } from '@tanstack/react-virtual';
import { create } from 'zustand';

interface NavigationState {
  activeId: string | undefined;
  categoryIndexes?: Record<string, number>;
  setActiveId: (id: string | undefined) => void;
  forceActiveId: string | undefined;
  setForceActiveId: (id: string | undefined) => void;
  virtualizer: Virtualizer<Window, Element> | undefined;
  setVirtualizerData: (
    value: Virtualizer<Window, Element>,
    categoryIndexes?: Record<string, number>
  ) => void;
  handleVirtualChange: (virtualizer: Virtualizer<Window, Element>) => void;
}

const calculateActiveId = (
  virtualizer: Virtualizer<Window, Element>,
  categoryIndexes: Record<string, number>
) => {
  const scrollY = virtualizer.scrollElement?.scrollY;
  const height = virtualizer.scrollRect.height;

  if (scrollY) {
    const item = virtualizer.getVirtualItemForOffset(
      scrollY + (height * 2) / 3
    );
    const entries = Object.entries(categoryIndexes).sort((a, b) => a[1] - b[1]);

    const lastIndex = entries[entries.length - 1]?.[1];
    if (lastIndex != null && item.index >= lastIndex) {
      return entries[entries.length - 1]?.[0];
    }
    const foundEntryIndex = Math.max(
      entries.findIndex(([, value]) => value >= item.index) - 1,
      0
    );
    return entries[foundEntryIndex]?.[0];
  }
  return undefined;
};

export const useNavigationState = create<NavigationState>((set, get) => ({
  activeId: undefined,
  forceActiveId: undefined,
  virtualizer: undefined,
  setActiveId: (activeId) => {
    const { virtualizer, categoryIndexes } = get();

    if (virtualizer && categoryIndexes && activeId) {
      const smoothScroll = () => {
        const nextIndex = categoryIndexes[activeId];
        const nextVirtualItem = virtualizer
          .getVirtualItems()
          .find((row) => row.index === nextIndex);

        if (nextIndex == null) return;
        const [nextOffset] = virtualizer.getOffsetForIndex(nextIndex, 'center');

        if (nextVirtualItem != null) {
          virtualizer.scrollElement?.scrollTo({
            top: nextOffset,
            behavior: 'smooth',
          });
          return;
        }

        virtualizer.scrollToIndex(nextIndex ?? 0, {
          align: 'center',
        });
      };
      smoothScroll();
    }
    set({ activeId, forceActiveId: undefined });
  },
  setForceActiveId: (forceActiveId) => set({ forceActiveId }),
  setVirtualizerData: (virtualizer, categoryIndexes) =>
    set({ virtualizer, categoryIndexes }),
  handleVirtualChange: (virtualizer: Virtualizer<Window, Element>) => {
    const { categoryIndexes, forceActiveId } = get();
    if (!categoryIndexes || forceActiveId) return;
    const nextActiveId = calculateActiveId(virtualizer, categoryIndexes);
    if (categoryIndexes) set({ activeId: nextActiveId });
  },
}));
